import type { HTMLAttributes } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

export interface ErpTableSkeletonProps extends HTMLAttributes<HTMLDivElement> {
  columns?: number;
  rows?: number;
  showToolbar?: boolean;
  toolbarButtons?: number;
}

const getCellWidthClass = (index: number) => {
  const widthClasses = ['w-[85%]', 'w-[90%]', 'w-[70%]', 'w-[75%]', 'w-[60%]', 'w-[80%]', 'w-[65%]'];
  return widthClasses[index % widthClasses.length];
};

export function ErpTableSkeleton({
  columns = 7,
  rows = 6,
  showToolbar = true,
  toolbarButtons = 2,
  className,
  ...props
}: ErpTableSkeletonProps) {
  return (
    <div className={cn('flex h-full flex-col border-t border-slate-200 bg-white', className)} {...props}>
      {showToolbar && (
        <div className='flex items-center gap-2 border-b border-slate-200 px-2 py-1'>
          {Array.from({ length: toolbarButtons }).map((_, index) => (
            <Skeleton key={`table-skeleton-toolbar-${index}`} className='h-7 w-7 rounded-sm' />
          ))}
        </div>
      )}

      <div className='border-b border-slate-200 px-3 py-3'>
        <div
          className='grid gap-3'
          style={{
            gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`
          }}
        >
          {Array.from({ length: columns }).map((_, index) => (
            <Skeleton key={`table-skeleton-header-${index}`} className='h-4 w-full rounded-sm' />
          ))}
        </div>
      </div>

      <div className='space-y-3 px-3 py-4'>
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div
            key={`table-skeleton-row-${rowIndex}`}
            className='grid gap-3'
            style={{
              gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`
            }}
          >
            {Array.from({ length: columns }).map((_, columnIndex) => (
              <Skeleton
                key={`table-skeleton-row-${rowIndex}-col-${columnIndex}`}
                className={cn('h-4 rounded-sm', getCellWidthClass(columnIndex))}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
